import React, { useEffect, useState } from "react";
import FirestoreService from "../services/firestoreService";
import PlayArrow from "@mui/icons-material/PlayArrow";
import { use } from "react";
import { formatDate } from "../utils";
import RemoteConfigService from "../services/remoteConfigService";
import { getUser, searchSongs } from "../spotify/index";
import { token } from "../spotify";
import { Alert } from "@material-tailwind/react";

export default function SongsOfTheDay() {
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [today, setToday] = useState("");
  const [promptOfTheDay, setPromptOfTheDay] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [songsFiltered, setSongsFiltered] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [userId, setUserId] = useState(null);
  const isAuthenticated = Boolean(token && token !== "undefined");

  async function addOrReplaceSong(song) {
    setSearchQuery("");
    setSongsFiltered([]);
    if (!userId) {
      try {
        const user = await getUser();
        setUserId(user.data.id);
        // Wait for userId to be set before proceeding (or use user.data.id directly)
        await FirestoreService.addOrReplaceSongForToday(song, user.data.id);
      } catch (error) {
        console.error("Error fetching user or adding song:", error);
      }
    } else {
      try {
        await FirestoreService.addOrReplaceSongForToday(song, userId);
      } catch (error) {
        console.error("Error adding or replacing song:", error);
      }
    }

    setShowDropdown(false);
  }

  function handleKeyDown(event) {
    if (event.key === "Enter") {
      event.preventDefault();
      search();
    } else if (event.key === "Escape") {
      event.preventDefault();
      setShowDropdown(false);
      setSearchQuery("");
      setSongsFiltered([]);
    }
    // Close dropdown on Escape key
    if (event.key === "Escape") {
      setShowDropdown(false);
      setSearchQuery("");
      setSongsFiltered([]);
    }
  }

  function search() {
    if (!searchQuery || searchQuery.trim() === "") {
      setSongsFiltered([]);
      setShowDropdown(false);
      return;
    }
    setSongsFiltered([]);
    setShowDropdown(true);
    if (searchQuery.trim() === "") {
      setSongsFiltered([]);
      return;
    }
    searchSongs(searchQuery)
      .then((results) => {
        setSongsFiltered(results);
        if (results.length > 0) {
          setShowDropdown(true);
        } else {
          setShowDropdown(false);
        }
      })
      .catch((error) => {
        console.error("Error searching songs:", error);
        setSongsFiltered([]);
      });
  }

  // Fetch the prompt of the day from remote config
  useEffect(() => {
    RemoteConfigService.fetchRemoteConfig()
      .then(() => {
        setPromptOfTheDay(
          RemoteConfigService.getParameter("daily_challenge") != ""
            ? RemoteConfigService.getParameter("daily_challenge")
            : "What song are you vibing to today?"
        );
      })
      .catch((error) => {
        console.error("Error fetching prompt of the day:", error);
        setPromptOfTheDay("What song are you vibing to today?");
      });
  }, []);

  // Update today's date to the current date
  useEffect(() => {
    // Function to update the date based on Trinidad and Tobago timezone
    function updateToday() {
      const now = new Date();
      const options = { timeZone: "America/Port_of_Spain" };
      const localDate = new Date(now.toLocaleString("en-US", { ...options }));
      setToday(formatDate(localDate));
    }

    updateToday(); // Set initially

    // Update at midnight in Trinidad and Tobago timezone
    const interval = setInterval(() => {
      updateToday();
    }, 60 * 1000); // Check every minute

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Subscribe to real-time updates
    const unsubscribe = FirestoreService.fetchSongsForToday(
      (fetchedSongs) => setSongs(fetchedSongs || []),
      setLoading
    );

    // Cleanup on unmount
    return () => {
      if (typeof unsubscribe === "function") {
        unsubscribe();
      }
    };
  }, []);

  // Helper to format "time ago"
  function timeAgo(timestamp) {
    if (!timestamp) return "";
    const now = Date.now();
    const diff = now - Number(timestamp);
    const seconds = Math.floor(diff / 1000);
    if (seconds < 60) return `${seconds} seconds ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} minutes ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hours ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days} days ago`;
    const weeks = Math.floor(days / 7);
    if (weeks < 4) return `${weeks} weeks ago`;
    const months = Math.floor(days / 30);
    return `${months} months ago`;
  }

  return (
    <div className="w-full mx-auto max-w-[1400px] min-h-screen p-6 sm:p-8 md:p-20">
      <h2 className="font-black text-2xl text-white mb-5">
        Songs of the Day for {today}
      </h2>
      <p className="text-white mb-10">{promptOfTheDay}</p>

      {isAuthenticated ? (
        <div className="relative  mt-6 w-full py-4">
          {/* Search Input */}
          <div className="flex items-center overflow-hidden rounded-lg bg-white shadow">
            <input
              id="search-box"
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search for a song..."
              className="w-full px-4 py-4 focus:outline-none focus:ring-2 focus:ring-green-500"
            />
            <button
              onClick={search}
              className="rounded-r-lg bg-green-500 px-4 py-4 text-white transition hover:bg-green-600"
            >
              Search
            </button>
          </div>

          {/* Dropdown with search results */}
          {showDropdown && (
            <div className="absolute left-0 top-full z-50 mt-2 w-full max-h-[50vh] sm:max-h-[400px] overflow-y-auto rounded-lg bg-white shadow-lg">
              {songsFiltered.length > 0 ? (
                songsFiltered.map((song, index) => (
                  <button
                    key={index}
                    type="button"
                    className="w-full border-t border-gray-200 px-4 py-3 text-left hover:bg-gray-100 focus:outline-none flex flex-row items-center"
                    onClick={() => addOrReplaceSong(song)}
                  >
                    {song.album &&
                      song.album.images &&
                      song.album.images[2] && (
                        <img
                          src={song.album.images[2].url}
                          alt="Album Artwork"
                          className="w-12 h-12 rounded mr-4 object-cover bg-gray-200 flex-shrink-0"
                        />
                      )}
                    <div className="flex flex-col min-w-0">
                      <p className="font-semibold text-gray-900 flex items-center">
                        {song.name}{" "}
                        {song.explicit === true ? (
                          <span
                            className="inline-flex items-center justify-center px-1.5 py-0.5 rounded bg-red-600 text-xs font-bold text-white ml-2"
                            title="Explicit"
                          >
                            E
                          </span>
                        ) : null}
                      </p>
                      <p className="text-sm text-gray-600 truncate">
                        by {song.artists.map((a) => a.name).join(", ")}
                      </p>
                      {song.album && (
                        <p className="text-xs text-gray-500 truncate">
                          Album: {song.album.name}
                        </p>
                      )}
                    </div>
                  </button>
                ))
              ) : (
                <div className="flex items-center justify-center p-6">
                  <div className="h-12 w-12 animate-spin rounded-full border-t-4 border-green-500"></div>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <p></p>
      )}
      {loading ? (
        <div role="status">
          <svg
            aria-hidden="true"
            class="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-[#1DB954]"
            viewBox="0 0 100 101"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
              fill="currentColor"
            />
            <path
              d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
              fill="currentFill"
            />
          </svg>
        </div>
      ) : (
        <ul className="list-none pb-40 m-0 ">
          {songs.length === 0 ? (
            <li className="text-white">
              Be the first to add your song of the day. None added yet
            </li>
          ) : (
            songs.map((song, idx) => (
              <li key={song.id || idx}>
                <div
                  className="flex flex-col md:flex-row items-center rounded-lg mb-4 no-underline transition-colors duration-200 p-3 bg-[#232323] hover:bg-[#333]"
                  style={{ textDecoration: "none" }}
                >
                  <div className="flex items-center w-full md:w-auto">
                    <span
                      className="w-10 h-10 pr-4 pl-2 md:pl-8 md-mr-4 border-none rounded-full flex items-center justify-center mr-0 sm:mr-4 mb-2 sm:mb-0 cursor-pointer transition-colors duration-200 group"
                      title="Play"
                      tabIndex={-1}
                      onClick={() => {
                        if (song.spotify_link) {
                          window.open(
                            song.spotify_link,
                            "_blank",
                            "noopener,noreferrer"
                          );
                        }
                      }}
                    >
                      <PlayArrow
                        style={{ fontSize: 32 }}
                        className="text-white group-hover:text-green-500 transition-colors duration-200"
                      />
                    </span>
                    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-md overflow-hidden mr-0 sm:mr-4 bg-[#333] flex items-center justify-center">
                      {song.song_album ? (
                        <img
                          src={song.song_album}
                          alt="Album Artwork"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-[#222]" />
                      )}
                    </div>
                    <div className="flex-1 flex flex-col sm:flex-row justify-between items-center sm:items-center min-w-0 w-full">
                      <span className="flex flex-col min-w-0 w-full sm:w-auto text-left md:text-left pl-4 gap-1">
                        <span className="font-bold text-base text-white whitespace-wrap">
                          {song.song_name}{" "}
                        </span>
                        <div className="text-[#b3b3b3] text-sm whitespace-wrap">
                          <span>{song.artist_name}</span>
                        </div>
                        <div className="text-[#b3b3b3] text-sm whitespace-wrap">
                          {
                            <>
                              added by {song.user?.displayName ?? song.added_by}{" "}
                            </>
                          }{" "}
                          {song.added_at && <>{timeAgo(song.added_at)}</>}
                        </div>
                      </span>
                    </div>
                  </div>
                </div>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
